import axios from 'axios'

export const FECHAMENTOS_PERMISSION = 'fechamentos-view'
export const MAX_PDF_SIZE = 30 * 1024 * 1024

export type Fechamento = {
  id: string
  title: string
  fileName: string
  fileSize: number
  pageCount: number
  createdAt: string
  createdByName: string
  isActive?: boolean
}

export type FechamentoPage = {pageNumber: number; width: number; height: number; dpi: number}
export type FechamentoDetail = Fechamento & {pages: FechamentoPage[]}
export type FechamentoList = {items: Fechamento[]; total: number; active?: Fechamento | null}
type ApiResponse<T> = {success: boolean; data: T; message?: string; error?: string}

function authHeaders() {
  const token = localStorage.getItem('token')
  return token ? {'x-access-token': token} : {}
}

function readableError(data: Partial<ApiResponse<unknown>>, fallback: string) {
  return new Error(
    data?.message ||
      (typeof data?.error === 'string' && !/^[A-Z0-9_]+$/.test(data.error) ? data.error : fallback)
  )
}

async function apiError(error: unknown, fallback: string): Promise<Error> {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data
    if (data instanceof Blob && data.size <= 64 * 1024) {
      try {
        return readableError(JSON.parse(await readBlobText(data)), fallback)
      } catch {
        return new Error(fallback)
      }
    }
    return data ? readableError(data, fallback) : new Error(fallback)
  }
  return error instanceof Error ? error : new Error(fallback)
}

function readBlobText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = reject
    reader.readAsText(blob)
  })
}

async function request<T>(operation: () => Promise<{data: ApiResponse<T>}>, fallback: string) {
  try {
    const response = await operation()
    if (!response.data.success) throw readableError(response.data, fallback)
    return response.data.data
  } catch (error) {
    throw await apiError(error, fallback)
  }
}

export function listFechamentos(search: string, offset: number, signal?: AbortSignal) {
  return request<FechamentoList>(
    () =>
      axios.get('/fechamentos', {
        params: {search, offset, limit: 12},
        headers: authHeaders(),
        signal,
      }),
    'Não foi possível carregar os fechamentos.'
  )
}

export function getFechamento(id: string, signal?: AbortSignal) {
  return request<FechamentoDetail>(
    () => axios.get('/fechamentos/' + encodeURIComponent(id), {headers: authHeaders(), signal}),
    'Não foi possível carregar o fechamento.'
  )
}

export function uploadFechamento(file: File, title: string, onProgress: (percent: number) => void) {
  const form = new FormData()
  form.append('file', file)
  form.append('title', title.trim())
  return request<FechamentoDetail>(
    () =>
      axios.post('/fechamentos', form, {
        headers: authHeaders(),
        timeout: 15 * 60 * 1000,
        onUploadProgress: (event) => {
          if (event.total) onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)))
        },
      }),
    'Não foi possível enviar o fechamento.'
  )
}

export function getFechamentoAtivo(signal?: AbortSignal) {
  return request<Fechamento | null>(
    () => axios.get('/fechamentos/ativo', {headers: authHeaders(), signal}),
    'Não foi possível carregar o fechamento ativo.'
  )
}

export function setFechamentoAtivo(id: string) {
  return request<FechamentoDetail>(
    () =>
      axios.put('/fechamentos/' + encodeURIComponent(id) + '/ativo', null, {
        headers: authHeaders(),
      }),
    'Não foi possível definir o fechamento ativo.'
  )
}

export function clearFechamentoAtivo() {
  return request<null>(
    () => axios.delete('/fechamentos/ativo', {headers: authHeaders()}),
    'Não foi possível remover o fechamento ativo.'
  )
}

async function loadFile(path: string, mimeType: string, signal?: AbortSignal): Promise<Blob> {
  const fallback = 'Não foi possível carregar o arquivo. Tente novamente.'
  try {
    const response = await axios.get<Blob>(path, {
      responseType: 'blob',
      headers: authHeaders(),
      signal,
    })
    const contentType = String(
      response.headers?.['content-type'] || response.data?.type || ''
    ).split(';')[0]
    if (contentType === 'application/json' && response.data.size <= 64 * 1024) {
      throw readableError(JSON.parse(await readBlobText(response.data)), fallback)
    }
    if (!(response.data instanceof Blob) || !response.data.size || contentType !== mimeType) {
      throw new Error(fallback)
    }
    return response.data
  } catch (error) {
    throw await apiError(error, fallback)
  }
}

export function loadFechamentoImage(
  id: string,
  page: number,
  thumbnail = false,
  signal?: AbortSignal
) {
  return loadFile(
    '/fechamentos/' + encodeURIComponent(id) + '/paginas/' + page + (thumbnail ? '/miniatura' : ''),
    'image/png',
    signal
  )
}

export function loadFechamentoPdf(id: string) {
  return loadFile('/fechamentos/' + encodeURIComponent(id) + '/pdf', 'application/pdf')
}

export function pageFileName(fileName: string, page: number) {
  return fileName.replace(/\.pdf$/i, '') + '-pagina-' + String(page).padStart(3, '0') + '.png'
}

export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  try {
    link.href = url
    link.download = Array.from(fileName, (character) =>
      character.charCodeAt(0) < 32 ? '-' : character
    )
      .join('')
      .replace(/[\\/:*?"<>|]/g, '-')
    document.body.appendChild(link)
    link.click()
  } finally {
    link.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
}
